import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Play, RotateCcw, ArrowLeft } from "lucide-react";

const css = `
@keyframes nl-sweep {
  0% { background-position: 140% 0; }
  100% { background-position: -140% 0; }
}
@keyframes nl-pulse {
  0%, 100% { opacity: 0.06; }
  50% { opacity: 0.18; }
}
@keyframes nl-line {
  0% { left: -30%; }
  100% { left: 105%; }
}
@keyframes nl-veil {
  0% { transform: translateX(-160%) skewX(-18deg); }
  100% { transform: translateX(420%) skewX(-18deg); }
}
.nl-n {
  -webkit-mask: url('/logo-icon.png') center / contain no-repeat;
  mask: url('/logo-icon.png') center / contain no-repeat;
}
.nl-n-shimmer {
  background-color: rgba(255,255,255,0.07);
  background-image: linear-gradient(105deg, rgba(255,255,255,0) 38%, rgba(255,255,255,0.38) 50%, rgba(255,255,255,0) 62%);
  background-size: 240% 100%;
  background-repeat: no-repeat;
  animation: nl-sweep 1.7s infinite linear;
}
.nl-n-ghost { background-color: rgba(255,255,255,0.07); }
.nl-n-pulse { background-color: rgba(255,255,255,0.12); animation: nl-pulse 1.8s ease-in-out infinite; }
.nl-skel {
  background-color: rgba(255,255,255,0.05);
  background-image: linear-gradient(100deg, rgba(255,255,255,0) 40%, rgba(255,255,255,0.14) 50%, rgba(255,255,255,0) 60%);
  background-size: 220% 100%;
  background-repeat: no-repeat;
  animation: nl-sweep 1.6s infinite linear;
  border-radius: 6px;
}
.nl-track { position: relative; overflow: hidden; background: rgba(255,255,255,0.06); border-radius: 99px; }
.nl-track::after {
  content: ""; position: absolute; top: 0; bottom: 0; width: 30%; border-radius: 99px;
  background: linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,0.45), rgba(255,255,255,0));
  animation: nl-line 1.4s infinite ease-in-out;
}
.nl-veil-band {
  position: absolute; top: -20%; bottom: -20%; width: 34%;
  background: linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,0.05) 35%, rgba(255,255,255,0.09) 50%, rgba(255,255,255,0.05) 65%, rgba(255,255,255,0));
  animation: nl-veil 2.4s infinite ease-in-out;
}
`;

const Cell = ({ n, title, sub, children }: { n: number; title: string; sub: string; children: React.ReactNode }) => (
  <div className="relative bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden h-[360px] flex flex-col">
    <div className="px-5 pt-4 flex items-start justify-between gap-3">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#00E5FF]">Option {n}</p>
        <h3 className="font-heading font-bold text-white text-lg mt-1">{title}</h3>
        <p className="text-gray-500 text-xs mt-1">{sub}</p>
      </div>
      <Link to={`/preloader-preview/${n}`} data-testid={`preloader-live-link-${n}`}
        className="shrink-0 mt-1 inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#7C3AED] hover:bg-[#6b2fd6] rounded-md px-3 py-1.5 transition-colors">
        <Play className="h-3 w-3" /> View live
      </Link>
    </div>
    <div className="flex-1 flex items-center justify-center relative overflow-hidden">{children}</div>
  </div>
);

const Loader = ({ option }: { option: number }) => {
  if (option === 1) return <div className="nl-n nl-n-shimmer w-32 h-32" />;
  if (option === 2)
    return (
      <div className="w-72 max-w-[85vw]">
        <div className="nl-n nl-n-ghost w-10 h-10 mx-auto mb-5" />
        <div className="nl-skel w-full aspect-[16/7] mb-3" />
        <div className="nl-skel h-4 w-11/12 mb-2" />
        <div className="nl-skel h-4 w-4/5 mb-4" />
        <div className="nl-skel h-3 w-2/5" />
      </div>
    );
  if (option === 3)
    return (
      <div className="flex flex-col items-center">
        <div className="nl-n nl-n-pulse w-28 h-28" />
        <div className="nl-track w-40 h-1 mt-6" />
      </div>
    );
  return (
    <>
      <div className="nl-n nl-n-ghost w-32 h-32" />
      <div className="nl-veil-band" />
    </>
  );
};

const DEMO_TITLES: Record<number, string> = {
  1: "Shimmer N", 2: "Article Skeleton Sweep", 3: "Breathing N + Sweep Line", 4: "Page Veil Sweep",
};

export const PreloaderDemo = () => {
  const { option } = useParams();
  const n = Math.min(4, Math.max(1, Number(option) || 1));
  const [loading, setLoading] = useState(true);
  const [run, setRun] = useState(0);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => setLoading(false), 2400);
    return () => clearTimeout(t);
  }, [run, n]);

  return (
    <div className="min-h-[70vh] relative" data-testid={`preloader-demo-${n}`}>
      <style>{css}</style>
      {loading && (
        <div className="fixed inset-0 z-[70] bg-[#050505] flex items-center justify-center overflow-hidden">
          <Loader option={n} />
        </div>
      )}
      {!loading && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 animate-in fade-in duration-500">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#00E5FF]">Simulated article load</p>
          <h1 className="font-heading font-black text-white text-3xl sm:text-4xl mt-2 leading-tight">
            This is what readers see once the page finishes loading
          </h1>
          <div className="w-full aspect-[16/8] bg-[#121212] border border-white/10 rounded-xl mt-6 flex items-center justify-center">
            <div className="nl-n nl-n-ghost w-20 h-20" />
          </div>
          <p className="text-gray-400 mt-6 leading-relaxed">
            The preloader you just watched — <span className="text-white font-bold">Option {n}: {DEMO_TITLES[n]}</span> — covered
            the screen while the page prepared its content, then faded out to reveal the article. Site-wide, it would run only
            as long as the data actually takes to load (usually well under a second).
          </p>
        </div>
      )}
      <div className="fixed z-[80] bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-[#121212] border border-white/15 rounded-full px-2 py-1.5 shadow-2xl shadow-black/60">
        <Link to="/preloader-preview" data-testid="preloader-demo-back"
          className="inline-flex items-center gap-1.5 text-xs text-gray-300 hover:text-white px-3 py-1.5 transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> All options
        </Link>
        {[1, 2, 3, 4].map((i) => (
          <Link key={i} to={`/preloader-preview/${i}`}
            className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${i === n ? "bg-[#7C3AED] text-white" : "text-gray-400 hover:text-white"}`}>
            {i}
          </Link>
        ))}
        <button onClick={() => setRun((r) => r + 1)} data-testid="preloader-demo-replay"
          className="inline-flex items-center gap-1.5 text-xs font-bold text-black bg-[#00E5FF] hover:bg-[#33eaff] rounded-full px-3 py-1.5 transition-colors">
          <RotateCcw className="h-3.5 w-3.5" /> Replay
        </button>
      </div>
    </div>
  );
};

export default function PreloaderPreview() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="preloader-preview-page">
      <style>{css}</style>
      <h1 className="font-heading font-black text-white text-3xl mb-2">Preloader Options</h1>
      <p className="text-gray-400 mb-8">Fox-style sweeping gray animations, NewsLayer edition — pick your favorite.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        <Cell n={1} title="Shimmer N" sub="The N in very light gray with a bright sweep passing through the letterform — Fox's sheen, inside the logo.">
          <div className="nl-n nl-n-shimmer w-32 h-32" />
        </Cell>

        <Cell n={2} title="Article Skeleton Sweep" sub="Fox's actual article preloader: ghost-gray content blocks with the diagonal sweep, crowned by a faint N.">
          <div className="w-72">
            <div className="nl-n nl-n-ghost w-10 h-10 mx-auto mb-5" />
            <div className="nl-skel w-full aspect-[16/7] mb-3" />
            <div className="nl-skel h-4 w-11/12 mb-2" />
            <div className="nl-skel h-4 w-4/5 mb-4" />
            <div className="nl-skel h-3 w-2/5" />
          </div>
        </Cell>

        <Cell n={3} title="Breathing N + Sweep Line" sub="The N gently pulses in pale gray while a light bar sweeps a thin track beneath it. Calm and minimal.">
          <div className="flex flex-col items-center">
            <div className="nl-n nl-n-pulse w-28 h-28" />
            <div className="nl-track w-40 h-1 mt-6" />
          </div>
        </Cell>

        <Cell n={4} title="Page Veil Sweep" sub="A static ghost N while a broad, barely-there gray band sweeps across the whole screen — the most 'Fox page load' feel.">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="nl-n nl-n-ghost w-32 h-32" />
            <div className="nl-veil-band" />
          </div>
        </Cell>

      </div>
    </div>
  );
}
